import React, { useMemo } from 'react'
import { type GridColumns, type GridColumn } from '@retailer-platform/shared-components'
import { Text } from '@retailer-platform/shared-components'
import { useDomainMessages } from 'domains/partner-pick-devices/utils/domain/intl'
import { type PartnerPickDeviceDatum } from 'domains/partner-pick-devices/types/partnerPickDevices.types'
import { PartnerPickDevicesListDeviceTypeCell } from 'domains/partner-pick-devices/components/list/components/table-cells/device-type/PartnerPickDevicesListDeviceTypeCell'
import { PartnerPickDevicesListStatusCell } from 'domains/partner-pick-devices/components/list/components/table-cells/status/PartnerPickDevicesListStatusCell'
import { PartnerPickDevicesListActionsCell } from 'domains/partner-pick-devices/components/list/components/table-cells/actions/PartnerPickDevicesListActionsCell'
import { PartnerPickDevicesListLoginCell } from 'domains/partner-pick-devices/components/list/components/table-cells/login/PartnerPickDevicesListLoginCell'
import { PartnerPickDevicesListCardStatusCell } from 'domains/partner-pick-devices/components/list/components/table-cells/card-status/PartnerPickDevicesListCardStatusCell'

interface UsePartnerPickDevicesListColumnsProps {
  onEdit: (id: string) => void
  onStatusUpdate: () => void
  showCardStatus: boolean
}

export const usePartnerPickDevicesListColumns = ({
  onEdit,
  onStatusUpdate,
  showCardStatus,
}: UsePartnerPickDevicesListColumnsProps) => {
  const columnHeaders = useDomainMessages({
    name: 'partnerPickDevicesDomain.list.columns.name',
    deviceType: 'partnerPickDevicesDomain.list.columns.deviceType',
    login: 'partnerPickDevicesDomain.list.columns.login',
    email: 'partnerPickDevicesDomain.list.columns.email',
    status: 'partnerPickDevicesDomain.list.columns.status',
    cardStatus: 'partnerPickDevicesDomain.list.columns.cardStatus',
  })

  const columns = useMemo(() => {
    const name: GridColumn<PartnerPickDeviceDatum> = {
      id: 'name',
      Header: columnHeaders.name,
      Cell: ({ row }) => <Text>{`${row.original.firstName} ${row.original.lastName}`}</Text>,
      width: 150,
    }

    const deviceType: GridColumn<PartnerPickDeviceDatum> = {
      id: 'deviceType',
      Header: columnHeaders.deviceType,
      Cell: ({ row }) => <PartnerPickDevicesListDeviceTypeCell partnerPickDevice={row.original} />,
      width: 75,
    }

    const login: GridColumn<PartnerPickDeviceDatum> = {
      id: 'login',
      Header: columnHeaders.login,
      Cell: ({ row }) => <PartnerPickDevicesListLoginCell partnerPickDevice={row.original} />,
      width: 100,
    }

    const email: GridColumn<PartnerPickDeviceDatum> = {
      id: 'email',
      Header: columnHeaders.email,
      Cell: ({ row }) => <Text>{row.original.email}</Text>,
      width: 150,
    }

    const status: GridColumn<PartnerPickDeviceDatum> = {
      id: 'status',
      Header: columnHeaders.status,
      Cell: ({ row }) => <PartnerPickDevicesListStatusCell partnerPickDevice={row.original} />,
      width: 75,
    }

    const cardStatus: GridColumn<PartnerPickDeviceDatum> = {
      id: 'cardStatus',
      Header: columnHeaders.cardStatus,
      Cell: ({ row }) => <PartnerPickDevicesListCardStatusCell partnerPickDevice={row.original} />,
      width: 75,
    }

    const actions: GridColumn<PartnerPickDeviceDatum> = {
      id: 'actions',
      Cell: ({ row }) => (
        <PartnerPickDevicesListActionsCell
          partnerPickDevice={row.original}
          onEdit={onEdit}
          onStatusUpdate={onStatusUpdate}
        />
      ),
      width: 100,
    }

    const columns: GridColumns<PartnerPickDeviceDatum> = [name, deviceType, login, email, status]

    if (showCardStatus) {
      columns.push(cardStatus)
    }

    columns.push(actions)

    return columns
  }, [columnHeaders, onEdit, onStatusUpdate, showCardStatus])

  return columns
}
